<template>
  <v-container fluid class="pa-0 product-info-form">
    <v-form ref="form" v-model="formValid" lazy-validation>
      <v-container fluid class="mb-3">
        <v-text-field
          rounded
          outlined
          dense
          label="Title"
          v-model="value.title"
          :rules="titleRules"
          required
        ></v-text-field>
      </v-container>
      <v-container
        fluid
        class="d-flex justify-space-between mb-3 flex-wrap flex-lg-nowrap product-info-grid"
      >
        <v-container fluid class="product-info-column">
          <v-text-field
            rounded
            outlined
            dense
            :rules="textRules"
            label="SKU Code"
            v-model="value.skuCode"
            required
          ></v-text-field>
          <v-autocomplete
            rounded
            outlined
            dense
            :rules="dropdownRules"
            :items="brands"
            item-text="displayName"
            item-value="id"
            label="Brand Name"
            v-model="value.brandId"
            required
          >
            <template v-slot:no-data>
              <v-card elevation="0">
                <v-card-text class="text-center">
                  <h4 class="mb-2">Brand name not found</h4>
                  <v-btn
                    depressed
                    small
                    rounded
                    color="primary"
                    @click="openDropdownOptionDialog(brandDropdownId)"
                  >
                    <i class="fas fa-plus mr-2"></i>
                    Add Brand
                  </v-btn>
                </v-card-text>
              </v-card>
            </template>
          </v-autocomplete>
          <v-autocomplete
            rounded
            outlined
            dense
            :rules="dropdownRules"
            :items="categories"
            item-text="displayName"
            item-value="id"
            label="Category"
            v-model="value.lvl1Id"
            required
          >
            <template v-slot:no-data>
              <v-card elevation="0">
                <v-card-text class="text-center">
                  <h4 class="mb-2">Category not found</h4>
                  <v-btn
                    depressed
                    small
                    rounded
                    color="primary"
                    @click="openDropdownOptionDialog(categoryDropdownId)"
                  >
                    <i class="fas fa-plus mr-2"></i>
                    Add Category
                  </v-btn>
                </v-card-text>
              </v-card>
            </template></v-autocomplete
          >
          <v-autocomplete
            rounded
            outlined
            dense
            :rules="dropdownRules"
            :items="productTypes"
            item-text="displayName"
            item-value="id"
            label="Type"
            v-model="value.lvl2Id"
            required
          >
            <template v-slot:no-data>
              <v-card elevation="0">
                <v-card-text class="text-center">
                  <h4 class="mb-2">Product type not found</h4>
                  <v-btn
                    depressed
                    small
                    rounded
                    color="primary"
                    @click="openDropdownOptionDialog(productTypeDropdownId)"
                  >
                    <i class="fas fa-plus mr-2"></i>
                    Add Type
                  </v-btn>
                </v-card-text>
              </v-card>
            </template></v-autocomplete
          >
          <v-autocomplete
            rounded
            outlined
            dense
            :items="[
              { val: true, text: 'Yes' },
              { val: false, text: 'No' },
            ]"
            item-text="text"
            item-value="val"
            label="Update Quantity Upon Order"
            v-model="value.updateQuantity"
          >
          </v-autocomplete>
          <v-autocomplete
            rounded
            outlined
            dense
            :items="images"
            item-text="id"
            label="Main Image"
            v-model="value.pictureId"
          >
            <template v-slot:item="{ item }">
              <v-container
                class="py-0 my-0 d-flex align-center justify-space-around"
              >
                <div class="subtitle-1">{{ item.id }}</div>
                <img
                  class="image-preview"
                  :src="getProductPictureUrlById(item.id)"
                />
              </v-container>
            </template>
          </v-autocomplete>
          <v-container class="d-flex align-center justify-space-around">
            <div class="form-section-title">Main Image Preview</div>
          </v-container>
        </v-container>
        <v-container fluid class="product-info-column">
          <v-text-field
            rounded
            outlined
            dense
            :rules="nonNegativeRules"
            prefix="$"
            label="Stock Price"
            v-model.number="value.stockPrice"
            required
          ></v-text-field>
          <v-text-field
            rounded
            outlined
            dense
            :rules="nonNegativeRules"
            label="Quantity"
            v-model.number="value.quantity"
            required
          ></v-text-field>
          <v-text-field
            rounded
            outlined
            dense
            :rules="nonNegativeRules"
            label="Quantity in US"
            v-model.number="value.usQuantity"
            required
            @change="updateQuantity"
          ></v-text-field>
          <v-text-field
            rounded
            outlined
            dense
            :rules="nonNegativeRules"
            label="Quantity in VN"
            v-model.number="value.vnQuantity"
            required
            @change="updateQuantity"
          ></v-text-field>

          <v-autocomplete
            rounded
            outlined
            dense
            :items="currencyTypes"
            :rules="dropdownRules"
            item-text="displayName"
            item-value="id"
            label="Currency Type"
            v-model.number="value.currencyId"
            required
          ></v-autocomplete>
          <v-text-field
            rounded
            outlined
            dense
            :rules="nonNegativeRules"
            :prefix="getCurrencySymbol(value.currencyId)"
            label="Unit Price"
            v-model.number="value.unitPrice"
          >
          </v-text-field>
          <img
            class="image-preview-lg"
            :src="getProductPictureUrlById(value.pictureId)"
          />
        </v-container>
        <v-container fluid class="product-info-column">
          <v-text-field
            rounded
            outlined
            dense
            :rules="nonNegativeRules"
            prefix="$"
            label="Retail Price"
            v-model.number="value.retailPrice"
            required
            @change="updateSalePrice"
          ></v-text-field>
          <v-text-field
            rounded
            outlined
            dense
            :rules="nonNegativeRules"
            prefix="$"
            label="Discount"
            v-model.number="value.discount"
            required
            @change="updateSalePrice"
          ></v-text-field>
          <v-text-field
            rounded
            outlined
            dense
            :rules="nonNegativeRules"
            prefix="$"
            label="Sale Price"
            v-model.number="value.salePrice"
            required
          ></v-text-field>
          <v-autocomplete
            rounded
            outlined
            dense
            :items="currencyTypes"
            :rules="dropdownRules"
            item-text="displayName"
            item-value="id"
            label="Air Currency Type"
            v-model.number="value.airCurrencyId"
            required
          ></v-autocomplete>
          <v-text-field
            rounded
            outlined
            dense
            :rules="nonNegativeRules"
            :prefix="getCurrencySymbol(value.airCurrencyId)"
            label="Air Price"
            v-model.number="value.airPrice"
          >
          </v-text-field>

          <v-autocomplete
            rounded
            outlined
            dense
            :items="currencyTypes"
            :rules="dropdownRules"
            item-text="displayName"
            item-value="id"
            label="Container Currency Type"
            v-model.number="value.containerCurrencyId"
            required
          ></v-autocomplete>
          <v-text-field
            rounded
            outlined
            dense
            :rules="nonNegativeRules"
            :prefix="getCurrencySymbol(value.containerCurrencyId)"
            label="Container Price"
            v-model.number="value.containerPrice"
          >
          </v-text-field>
        </v-container>
        <v-container fluid class="product-info-column">
          <v-text-field
            rounded
            outlined
            dense
            :rules="nonNegativeRules"
            label="Unit/Case"
            v-model.number="value.unitPerCase"
          ></v-text-field>
          <v-text-field
            rounded
            outlined
            dense
            :rules="nonNegativeRules"
            label="Case/Pallet"
            v-model.number="value.casePerPallet"
          ></v-text-field>
          <v-text-field
            rounded
            outlined
            dense
            :rules="nonNegativeRules"
            label="Case/Tier"
            v-model.number="value.casePerTier"
          ></v-text-field>
          <v-text-field
            rounded
            outlined
            dense
            :rules="nonNegativeRules"
            label="Pallet Quantity"
            v-model.number="value.palletQuantity"
          ></v-text-field>
          <v-text-field
            rounded
            outlined
            dense
            :rules="nonNegativeRules"
            label="Pallet's Height"
            v-model.number="value.palletHeight"
          ></v-text-field>
          <v-text-field
            rounded
            outlined
            dense
            :rules="nonNegativeRules"
            label="Size"
            v-model.number="value.size"
          ></v-text-field>
          <date-picker v-model="value.expDate" label="Exp Date"></date-picker>
        </v-container>
      </v-container>
      <v-container fluid :class="ckeditorClass">
        <div class="form-section-title mb-2">Product Description</div>
        <ckeditor
          :editor="editor"
          :config="editorConfig"
          v-model="value.description"
        ></ckeditor>
      </v-container>
    </v-form>
  </v-container>
</template>
<script>
import backendService from "@/services/backend-service";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import DatePicker from "@/components/DatePicker.vue";
import { CurrencyType } from "@/js/enum";
import generalMixin from "@/js/general-mixin";
import Decimal from "decimal.js";
import eventBus from "@/js/event-bus";
export default {
  name: "ProductInfoForm",
  mixins: [generalMixin],
  components: {
    "date-picker": DatePicker,
  },
  props: {
    product: {
      type: Object,
      default: () => {
        return {};
      },
    },
    images: {
      type: Array,
      default: () => {
        return [];
      },
    },
    pFormValid: {
      type: Boolean,
      default: () => {
        return false;
      },
    },
  },
  data() {
    return {
      dialog: false,
      dropdownId: -1,
      editor: ClassicEditor,
      editorConfig: {
        toolbar: ["bold", "italic", "bulletedList", "numberedList"],
      },
      //formValid: false,
      dropdownRules: [
        (v) => {
          if (v) return true;
          return "Required";
        },
      ],
      numberRules: [
        (v) => {
          if (v || v == 0) return true;
          return "Required";
        },
        (v) => {
          if (!isNaN(v)) return true;
          return "Must be numeric";
        },
        (v) => {
          if (v > 0) return true;
          return "Must be greater than 0";
        },
      ],
      nonNegativeRules: [
        (v) => {
          if (v || v == 0) return true;
          return "Required";
        },
        (v) => {
          if (!isNaN(v)) return true;
          return "Must be numeric";
        },
      ],
      textRules: [
        (v) => {
          if (v) return true;
          return "Required";
        },
      ],

      titleRules: [
        (v) => {
          if (v) return true;
          return "Required";
        },
        (v) => {
          if (v?.length >= 10) return true;
          return "Length must be at least 10 characters";
        },
      ],
    };
  },
  computed: {
    brands() {
      if (this.$store.getters["dropdownToOptions"]["brand"])
        return this.$store.getters["dropdownToOptions"]["brand"].options;
      return [];
    },
    brandDropdownId() {
      if (this.$store.getters["dropdownToOptions"]["brand"])
        return this.$store.getters["dropdownToOptions"]["brand"].id;
      return -1;
    },
    ckeditorClass() {
      return {
        "ckeditor-error": !this.descriptionValid,
      };
    },

    descriptionValid() {
      if (this.product.description?.length != 0) return true;
      return false;
    },

    formValid: {
      get() {
        return this.pFormValid;
      },
      set(val) {
        this.$emit("form-valid", val);
      },
    },
    value: {
      get() {
        return this.product;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    categories() {
      if (this.$store.getters["dropdownToOptions"]["category"])
        return this.$store.getters["dropdownToOptions"]["category"].options;
      return [];
    },
    currencyTypes() {
      if (this.$store.getters["dropdownToOptions"]["currency"])
        return this.$store.getters["dropdownToOptions"]["currency"].options;
      return [];
    },
    categoryDropdownId() {
      if (this.$store.getters["dropdownToOptions"]["category"])
        return this.$store.getters["dropdownToOptions"]["category"].id;
      return -1;
    },
    productTypes() {
      if (this.$store.getters["dropdownToOptions"]["productType"])
        return this.$store.getters["dropdownToOptions"]["productType"].options;
      return [];
    },
    productTypeDropdownId() {
      if (this.$store.getters["dropdownToOptions"]["productType"])
        return this.$store.getters["dropdownToOptions"]["productType"].id;
      return -1;
    },
    stores() {
      return this.$store.getters["stores"];
    },
  },

  methods: {
    getProductPictureUrlById(imageId) {
      if (!imageId) return;
      return backendService.getProductPictureUrlById(imageId);
    },
    validate() {
      return this.$refs.form.validate();
    },
    resetValidation() {
      this.$refs.form.resetValidation();
    },
    updateQuantity() {
      this.value.quantity = this.value.usQuantity + this.value.vnQuantity;
    },
    getCurrencySymbol(currencyId) {
      if (!currencyId) {
        return "";
      }
      let us = this.getIdByEnum(CurrencyType.UsDollar);
      let viet = this.getIdByEnum(CurrencyType.VietDong);
      if (currencyId == us) {
        return "$";
      } else if (currencyId == viet) {
        return "₫";
      }
      return "";
    },
    openDropdownOptionDialog(dropdownId) {
      eventBus.$emit("setDropdownOptionDialog", dropdownId);
    },
    updateSalePrice() {
      this.value.salePrice = new Decimal(this.value.retailPrice).minus(
        new Decimal(this.value.discount)
      );
    },
  },
};
</script>
<style scoped>
.product-info-form {
  max-width: none;
}
.product-info-grid {
  gap: 4px;
}
.product-info-column {
  flex: 1 1 240px;
  max-width: none;
  min-width: 0;
  padding-left: 0;
  padding-right: 0;
}
.ckeditor-error {
  border: 1px solid lightcoral !important;
}
.image-preview {
  object-fit: contain;
  image-rendering: smooth;
  width: 60px;
  height: 60px;
}
.image-preview-lg {
  object-fit: contain;
  image-rendering: smooth;
  width: 100px;
  height: 100px;
}
</style>
